import React from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { ArrowRight, Users, X } from "lucide-react";

const TYPE_COLORS = {
  rassemblement: "#FE2A88",
  blocage: "#000000",
  manifestation: "#FFCC00",
  petition: "#00C2CB",
  outil: "#33B871",
  autre: "#6E7BFF",
  mobilisation: "#FE2A88",
};

function colorIcon(color) {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="width:16px;height:16px;border:2px solid #000;border-radius:50%;background:${color};box-shadow:2px 2px 0 #000"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

function groupIcon() {
  return L.divIcon({
    className: "custom-group-marker",
    html: `<div style="width:18px;height:18px;border:2px solid #000;background:#33B871;box-shadow:2px 2px 0 #000"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
}

/**
 * Inline FWB map that expands below the homepage Quick Access section.
 * Lightweight version of /carte: shows initiatives + local groups markers only.
 * "Plus de détails" CTA routes to /carte for the full experience (filters, agenda…).
 */
export default function InlineFwbMap({ onClose }) {
  const [items, setItems] = React.useState([]);
  const [groups, setGroups] = React.useState([]);

  React.useEffect(() => {
    api.get("/initiatives").then((r) => setItems(r.data)).catch(() => {});
    api.get("/local-groups").then((r) => setGroups(r.data)).catch(() => {});
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6 animate-in fade-in slide-in-from-top-2" data-testid="inline-fwb-map">
      <div className="neo-card p-4 sm:p-6 bg-white">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter uppercase">Carte FWB — vue rapide</h2>
            <p className="text-sm text-neutral-600 mt-1">{items.length} actions · {groups.length} groupes locaux</p>
          </div>
          <div className="flex gap-2">
            <Link to="/carte" className="neo-btn bg-cef-blue text-white text-sm py-2" data-testid="inline-map-details">
              Plus de détails (actions & groupes locaux) <ArrowRight className="w-4 h-4" />
            </Link>
            <button type="button" onClick={onClose} className="neo-btn bg-white text-sm py-2 px-3" data-testid="inline-map-close" aria-label="Fermer">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="h-[420px] sm:h-[480px] border-2 border-black rounded-md overflow-hidden shadow-brutal">
          <MapContainer center={[50.6, 4.7]} zoom={8} style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {items.filter((i) => i.lat && i.lng).map((i) => (
              <Marker key={i.id} position={[i.lat, i.lng]} icon={colorIcon(TYPE_COLORS[i.type] || "#000")}>
                <Popup>
                  <div className="font-bold">{i.name}</div>
                  <div className="text-xs text-neutral-600">{i.school ? `${i.school} · ` : ""}{i.city}{i.date ? ` · ${i.date.slice(0, 10)}` : ""}</div>
                  {i.description && <div className="mt-1 text-sm">{i.description}</div>}
                </Popup>
              </Marker>
            ))}
            {groups.filter((g) => g.lat && g.lng).map((g) => (
              <Marker key={`grp-${g.id}`} position={[g.lat, g.lng]} icon={groupIcon()}>
                <Popup>
                  <div className="font-bold inline-flex items-center gap-1"><Users className="w-3 h-3" /> {g.name}</div>
                  {g.city && <div className="text-xs text-neutral-600">{g.city}{g.province ? ` · ${g.province}` : ""}</div>}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        <div className="mt-3 flex flex-wrap gap-3 text-xs">
          {Object.entries(TYPE_COLORS).slice(0, 4).map(([k, c]) => (
            <div key={k} className="inline-flex items-center gap-1.5"><span className="w-3 h-3 border-2 border-black rounded-full" style={{ background: c }} /> {k}</div>
          ))}
          <div className="inline-flex items-center gap-1.5"><span className="w-3 h-3 border-2 border-black bg-cef-green" /> Groupe local</div>
        </div>
      </div>
    </section>
  );
}
